import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import config from '../config';
import '../styles/ForwardPicker.css';

/**
 * ForwardPicker
 * A modal that lets the user pick one or more destinations (1:1 chats and/or
 * groups) to forward the selected message(s) to.
 *
 * Props:
 *  - isVisible: boolean
 *  - user: current user ({ id, ... })
 *  - messageCount: number of messages being forwarded (for the header)
 *  - onClose: () => void
 *  - onForward: (targets) => void  // targets: [{ type: 'user'|'group', id, name }]
 */
function ForwardPicker({ isVisible, user, messageCount = 1, onClose, onForward }) {
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Selected keys are strings like `user:<id>` / `group:<id>`
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (isVisible && user?.id) {
      fetchTargets();
    }
  }, [isVisible, user]);

  useEffect(() => {
    if (isVisible) {
      setSearchQuery('');
      setSelected({});
    }
  }, [isVisible]);

  const fetchTargets = async () => {
    setLoading(true);
    try {
      const [usersRes, groupsRes] = await Promise.all([
        axios.get(`${config.apiUrl}/api/auth/users-with-messages/${user.id}`),
        axios.get(`${config.apiUrl}/api/groups/user/${user.id}`),
      ]);
      setContacts(Array.isArray(usersRes.data) ? usersRes.data : []);
      setGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
    } catch (error) {
      console.error('Error fetching forward targets:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredContacts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) => (c.username || '').toLowerCase().includes(q));
  }, [contacts, searchQuery]);

  const filteredGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => (g.name || '').toLowerCase().includes(q));
  }, [groups, searchQuery]);

  const toggleSelect = (type, id, name) => {
    const key = `${type}:${id}`;
    setSelected((prev) => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = { type, id, name };
      }
      return next;
    });
  };

  const selectedList = Object.values(selected);

  const handleForward = () => {
    if (selectedList.length === 0) return;
    onForward(selectedList);
    setSelected({});
    onClose();
  };

  const handleClose = () => {
    setSelected({});
    setSearchQuery('');
    onClose();
  };

  if (!isVisible) return null;

  const hasResults = filteredContacts.length > 0 || filteredGroups.length > 0;

  return (
    <div className="forward-picker-overlay" onClick={handleClose}>
      <div className="forward-picker" onClick={(e) => e.stopPropagation()}>
        <div className="forward-picker-header">
          <h3>Forward to...</h3>
          <button className="forward-close-btn" onClick={handleClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="forward-search-container">
          <input
            type="text"
            placeholder="Search people and groups..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="forward-search-input"
          />
        </div>

        <div className="forward-picker-content">
          {loading ? (
            <div className="forward-loading-state">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="forward-spinning">
                <path d="M21 12a9 9 0 11-6.219-8.56" />
              </svg>
              <p>Loading chats...</p>
            </div>
          ) : !hasResults ? (
            <div className="forward-empty-state">
              <p>{searchQuery ? `No results for "${searchQuery}"` : 'No chats available'}</p>
            </div>
          ) : (
            <>
              {filteredGroups.length > 0 && (
                <div className="forward-section">
                  <div className="forward-section-title">Groups</div>
                  {filteredGroups.map((group) => {
                    const key = `group:${group._id}`;
                    const isSelected = !!selected[key];
                    return (
                      <div
                        key={key}
                        className={`forward-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleSelect('group', group._id, group.name)}
                      >
                        <div className="forward-avatar">
                          {group.avatar ? (
                            <img src={`${config.apiUrl}${group.avatar}`} alt={group.name} />
                          ) : (
                            <div className="forward-avatar-placeholder group notranslate">
                              {(group.name || 'G').charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="forward-info">
                          <div className="forward-name notranslate">{group.name}</div>
                          <div className="forward-sub">
                            {group.members?.length || 0} members
                          </div>
                        </div>
                        <div className={`forward-checkbox ${isSelected ? 'checked' : ''}`}>
                          {isSelected && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <path d="M20 6L9 17l-5-5" />
                            </svg>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {filteredContacts.length > 0 && (
                <div className="forward-section">
                  <div className="forward-section-title">Chats</div>
                  {filteredContacts.map((contact) => {
                    const key = `user:${contact.id}`;
                    const isSelected = !!selected[key];
                    return (
                      <div
                        key={key}
                        className={`forward-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleSelect('user', contact.id, contact.username)}
                      >
                        <div className="forward-avatar">
                          {contact.profileImage ? (
                            <img src={`${config.apiUrl}${contact.profileImage}`} alt={contact.username} />
                          ) : (
                            <div className="forward-avatar-placeholder notranslate">
                              {(contact.username || '?').charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="forward-info">
                          <div className="forward-name notranslate">{contact.username}</div>
                        </div>
                        <div className={`forward-checkbox ${isSelected ? 'checked' : ''}`}>
                          {isSelected && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <path d="M20 6L9 17l-5-5" />
                            </svg>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {selectedList.length > 0 && (
          <div className="forward-picker-footer">
            <div className="forward-selected-summary">
              {selectedList.length} selected
            </div>
            <button className="forward-send-btn" onClick={handleForward}>
              <span>
                Forward{messageCount > 1 ? ` ${messageCount} messages` : ''}
              </span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ForwardPicker;
